import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone, Mail } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";
import { SERVICES, FIRM } from "@/lib/content";

export default function ServiceDetail({ slug }) {
  const service = SERVICES.find((s) => s.slug === slug);
  useSEO({
    title: service ? `${service.title}, Bellhouse Law` : "Service",
    description: service?.short,
  });

  if (!service) {
    return (
      <div className="container-narrow py-40 text-center">
        <h1 className="text-3xl font-bold">Service not found</h1>
        <Link to="/services" className="mt-4 inline-block text-sage-700 underline">
          Back to all services
        </Link>
      </div>
    );
  }

  const hasFaqs = Array.isArray(service.faqs) && service.faqs.length > 0;
  const contact = service.contact;

  return (
    <div data-testid={`service-detail-${service.slug}`}>
      <PageHero
        eyebrow={`Practice Area`}
        title={service.title}
        subtitle={service.short}
        image={service.image}
        testId={`service-hero-${service.slug}`}
      />

      {/* Overview */}
      <section className="section-pad bg-bone">
        <div className="container-narrow grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-20">
          <div className="md:col-span-5">
            <span className="eyebrow">Overview</span>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-ink md:text-4xl lg:text-[42px]">
              {service.overviewHeading || "How we work alongside you."}
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-xl font-medium leading-snug text-ink md:text-2xl">
              {service.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Key services */}
      <section className="section-pad bg-sage-50/60">
        <div className="container-narrow">
          <div className="max-w-2xl">
            <span className="eyebrow">Key Services</span>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-ink md:text-4xl lg:text-[42px]">
              {service.keyServicesHeading || "What we routinely advise on."}
            </h2>
          </div>
          <ul className="mt-12 grid grid-cols-1 gap-x-10 gap-y-5 md:grid-cols-2">
            {service.keyServices.map((k) => (
              <li
                key={k}
                className="flex items-start gap-4 border-b border-sage-100 pb-5"
                data-testid={`key-service-${service.slug}`}
              >
                <span className="mt-1 grid h-6 w-6 flex-shrink-0 place-items-center border border-sage-300 text-sage-700">
                  <Check className="h-3 w-3" strokeWidth={2} />
                </span>
                <span className="text-base text-ink">{k}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-pad bg-bone">
        <div className="container-narrow grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-5">
            <span className="eyebrow">Why Bellhouse Law</span>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-ink md:text-4xl lg:text-[42px]">
              The Bellhouse Difference.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {service.benefits.map((b, i) => (
              <div
                key={b}
                className="border border-sage-100 bg-white p-7"
                data-testid={`benefit-${service.slug}-${i}`}
              >
                <div className="text-3xl font-bold text-sage-600">
                  0{i + 1}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">
                  {b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs — only if defined */}
      {hasFaqs && (
        <section className="section-pad bg-sage-50/60">
          <div className="container-tight">
            <div className="max-w-2xl">
              <span className="eyebrow">FAQs</span>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-ink md:text-4xl lg:text-[42px]">
                Common questions, candid answers.
              </h2>
            </div>
            <Accordion
              type="single"
              collapsible
              className="mt-10 w-full"
              data-testid={`faq-accordion-${service.slug}`}
            >
              {service.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-sage-100"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${service.slug}-${i}`}
                    className="py-6 text-left text-xl font-semibold text-ink hover:no-underline md:text-2xl"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-ink-soft md:text-lg">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Intake form + direct contact */}
      <section
        className={`section-pad ${hasFaqs ? "bg-bone" : "bg-sage-50/60"}`}
        data-testid={`service-intake-${service.slug}`}
      >
        <div className="container-narrow grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-4">
            <span className="eyebrow">Make an Enquiry</span>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-ink md:text-4xl lg:text-[42px]">
              Have a {service.title.toLowerCase()} matter?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-soft md:text-lg">
              Complete the secure intake form and a member of our team will be
              in touch shortly.
            </p>

            {contact && (
              <div
                className="mt-10 border border-sage-100 bg-white p-7"
                data-testid={`service-contact-${service.slug}`}
              >
                <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-sage-700">
                  <div>{contact.area}</div>
                  <div>Enquiries</div>
                </div>
                <div className="mt-4 text-lg font-bold text-ink">
                  {contact.name}
                </div>
                <div className="mt-4 space-y-2 text-sm text-ink-soft">
                  <a
                    href={`tel:${contact.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-2 hover:text-sage-700"
                  >
                    <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {contact.phone}
                  </a>
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2 hover:text-sage-700"
                  >
                    <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {contact.email}
                  </a>
                </div>
              </div>
            )}

            <div className="mt-8">
              <Link
                to="/contact"
                data-testid={`service-contact-page-${service.slug}`}
                className="inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.18em] text-sage-700 hover:gap-3"
              >
                View full contact details <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="border border-sage-100 bg-white p-3 shadow-[0_24px_60px_-30px_rgba(26,30,25,0.18)] md:p-4">
              <iframe
                title={`Bellhouse Law, ${service.title} Enquiry`}
                src={FIRM.intakeIframe}
                data-testid={`matter-intake-iframe-${service.slug}`}
                style={{
                  display: "block",
                  width: "100%",
                  height: "900px",
                  border: "none",
                }}
              />
            </div>
            <p className="mt-4 text-xs text-ink-muted">
              Secure intake hosted by matter-intake.com.au. Your information is
              encrypted in transit.
            </p>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
